// neo_cogym — window.electronAPI 브라우저 shim (W2 핵심)
// 코딩스쿨 렌더러(scratch-gui)는 preload가 노출한 window.electronAPI.ipcRenderer로
// 메인 프로세스와 통신한다(T1 분석: 범용 send/sendSync/invoke/on 브리지).
// 웹에선 메인 프로세스가 없으므로, 동일 채널을 받아 우리 웹 모듈로 라우팅하는
// shim 을 window.electronAPI 에 주입한다 → 렌더러 소스를 거의 고치지 않고 웹에서 동작.

import { CodewizDevice, PARTITION, type ConnectMode } from "../device/codewiz";
import type { RelayConfig } from "../device/relayTransport";
import { kv } from "../storage/kvStore";
import { askLuka } from "../ai/lukaClient";
import { fetchGuideFiles, guideAssetUrl } from "../content/contentService";
import { projectStore } from "../storage/projectStore";
import { captureToDataUrl } from "../vision/visionBridge";
import { flashCodewizFirmware, BOOTLOADER_CMD } from "../device/codewizFlasher";

type Listener = (event: unknown, ...args: unknown[]) => void;
type ExtentionsData = {
  extTypeList?: unknown[];
  extBlockList?: Record<string, unknown>;
  examTypeList?: unknown[];
  examList?: Record<string, unknown>;
};

export interface ShimOptions {
  /** iPad 등 WebSerial 미지원 시 사용할 릴레이 설정 */
  relay?: RelayConfig;
  getAuthToken?: () => string | null;
  /** shim 을 주입할 window(기본 현재 window). scratch-gui iframe 의 contentWindow 주입용 */
  targetWindow?: Window;
  /** 코드위즈 연결 상태 변화를 셸 UI 로 전달 */
  onDeviceStatus?: (s: string) => void;
}

export interface ShimHandle {
  device: CodewizDevice;
}

export function installElectronApiShim(opts: ShimOptions = {}): ShimHandle | void {
  const target = opts.targetWindow ?? (typeof window !== "undefined" ? window : undefined);
  if (!target) return;
  const listeners = new Map<string, Set<Listener>>();
  const handlers = new Map<string, (...args: unknown[]) => unknown>();

  const emit = (channel: string, ...args: unknown[]) => {
    listeners.get(channel)?.forEach((l) => l({}, ...args));
  };
  let connectedBoard: string | null = null;
  let connectedApp = -1;

  // 디바이스: 출력은 "pythonCallback" 채널로 렌더러에 흘려보냄(원본과 동일)
  const device = new CodewizDevice({
    onOutput: (t) => emit("pythonCallback", t),
    onStatus: (s) => {
      emit("codewizStatus", s);
      opts.onDeviceStatus?.(s);
    },
  });

  const connectMode = (): ConnectMode =>
    opts.relay ? { via: "relay", relay: opts.relay } : CodewizDevice.recommend();

  const markFirmwareChecked = () => {
    const maybeVm = (target as Window & {
      vm?: { connectManager?: { _isCH3Firmware?: boolean } };
    }).vm;
    if (maybeVm?.connectManager) {
      maybeVm.connectManager._isCH3Firmware = true;
    }
  };

  let extentionsCache: ExtentionsData | null = null;
  const getExtentions = async (): Promise<ExtentionsData> => {
    if (!extentionsCache) {
      const res = await fetch("/scratch-gui/static/serve/extentions.json", { cache: "no-cache" });
      if (!res.ok) {
        throw new Error(`extentions.json 로드 실패: HTTP ${res.status}`);
      }
      extentionsCache = (await res.json()) as ExtentionsData;
    }
    return extentionsCache;
  };

  const findExtensionType = (extensionName: string, extBlockList: Record<string, unknown>): string | null => {
    return Object.keys(extBlockList).find((key) => {
      const list = extBlockList[key];
      return key !== "all" && Array.isArray(list) && list.includes(extensionName);
    }) ?? null;
  };

  // ── 채널 라우팅 등록 ──────────────────────────────────────────────
  // 비동기 패턴: 렌더러가 send(ch, replyChannel, ...args) 후 on(replyChannel)로 결과 수신
  const asyncRoutes: Record<string, (args: unknown[]) => Promise<unknown>> = {
    "connect-async": async (a) => {
      const board = String(a[0] ?? "CODEWIZ");
      const appNum = Number(a[1] ?? -1);
      await device.connect(connectMode());
      if (appNum === 0) await device.selectPartition(PARTITION.runApp0);
      else if (appNum === 2) await device.selectPartition(PARTITION.runApp2);
      connectedBoard = board;
      connectedApp = Number.isFinite(appNum) ? appNum : -1;
      markFirmwareChecked();
      return null; // err 없음
    },
    "disconnect-async": async () => {
      await device.disconnect();
      connectedBoard = null;
      connectedApp = -1;
      return null;
    },
    "setConnectedApp-async": async (a) => {
      const appNum = Number(a[0] ?? -1);
      if (device.connected) {
        if (appNum === 0) await device.selectPartition(PARTITION.runApp0);
        else if (appNum === 1) await device.selectPartition(PARTITION.runApp1);
        else if (appNum === 2) await device.selectPartition(PARTITION.runApp2);
      }
      connectedApp = Number.isFinite(appNum) ? appNum : connectedApp;
      return null;
    },
    "connectPyboard-async": async () => {
      if (!device.connected) await device.connect(connectMode());
      return null;
    },
    "disconnectPyboard-async": async () => {
      await device.disconnect();
      return { head: "DisconnectHead", body: "DisconnectSuccessBody" };
    },
    "pythonUpload-async": async (a) => {
      return device.save(String(a[0] ?? ""));
    },
    // 플래시/컴파일: 웹은 CodeWiz 펌웨어 업로드만 WebSerial+esptool-js로 지원.
    "codeUpload-async": async () => "WEB_UNSUPPORTED_COMPILE",
    "fmUpload-async": async (a) => {
      const logs: string[] = [];

      // 연결 안 된 상태에서 업로드 시도 차단
      if (!device.connected) {
        return "[Connect] → [코드위즈 연결]을 먼저 클릭해 주세요.\n펌웨어 업로드는 코드위즈가 연결된 상태에서만 가능합니다.";
      }

      let bootloaderReady = false;

      // MicroPython에 연결된 상태에서 직접 ROM 부트로더 진입 명령 실행.
      // stop()으로 실행 중인 코드를 먼저 중단 → REPL 프롬프트 확보.
      // repl.run()은 코드를 전송하고 즉시 반환(실행 완료 대기 안 함).
      // machine.reset() 이후 장치가 ROM 다운로드 모드로 재시작.
      logs.push("[flash] MicroPython → ROM 부트로더 진입...");
      try {
        await device.stop().catch(() => {}); // 실행 중인 코드 중단 (Ctrl+C)
        await new Promise<void>((r) => setTimeout(r, 300));
        await device.run(BOOTLOADER_CMD);
      } catch { /* machine.reset() 으로 연결 끊김 — 정상 */ }
      await new Promise<void>((r) => setTimeout(r, 3000)); // USB 재연결 + ROM 부트로더 안정화 대기
      bootloaderReady = true;

      // 포트 해제 (이미 reset된 경우 close가 실패해도 무시)
      await device.disconnect().catch(() => {});
      await new Promise<void>((r) => setTimeout(r, 500));

      try {
        await flashCodewizFirmware({
          board: String(a[0] ?? "CODEWIZ"),
          bootloaderReady,
          onProgress: (p) => emit("progressUpdate", p),
          onLog: (line) => {
            console.log("[codewizFlasher]", line);
            logs.push(line);
          },
        });
        return null;
      } catch (e) {
        const lastLogs = logs.slice(-8).join(" → ");
        const msg = (e as Error).message || String(e);
        return lastLogs ? `${msg}\n[단계: ${lastLogs}]` : msg;
      }
    },
    "etcUpload-async": async () => "WEB_UNSUPPORTED_COMPILE",
  };

  // 동기/단방향 send 패턴
  const sendRoutes: Record<string, (args: unknown[]) => void> = {
    write: (a) => void device.run(decodeWrite(a[0])), // raw write는 run으로 근사
    pythonRun: (a) => void device.run(String(a[0] ?? "")),
    pythonPause: () => void device.stop(),
    "cs-window-control": () => {}, // 브라우저: 무의미
  };

  // 콘텐츠/가이드(W5): send(채널, {contentPath, tabType}) → emit(채널+Response, ...)
  // 코딩스쿨 IPCinit.js 의 getGuideFiles/getGuideFilePath/checkGuideExists/readTextFile 치환.
  const replyRoutes: Record<string, (arg: { contentPath: string; tabType?: string; filePath?: string }) => Promise<void>> = {
    getGuideFiles: async ({ contentPath, tabType }) => {
      const files = await fetchGuideFiles(contentPath, tabType, { getAuthToken: opts.getAuthToken });
      emit("getGuideFilesResponse", files);
    },
    getGuideFilePath: async ({ contentPath, tabType }) => {
      emit("getGuideFilePathResponse", `/content/guides/${contentPath}${tabType ? `/${tabType}` : ""}`);
    },
    checkGuideExists: async ({ contentPath }) => {
      const files = await fetchGuideFiles(contentPath, undefined, { getAuthToken: opts.getAuthToken });
      emit("checkGuideExistsResponse", { hasGuide: files.length > 0, hasSubFolders: false });
    },
    readTextFile: async ({ filePath }) => {
      try {
        const res = await fetch(guideAssetUrl("", String(filePath)));
        emit("readTextFileResponse", res.ok ? await res.text() : "");
      } catch {
        emit("readTextFileResponse", "");
      }
    },
  };

  // 프로젝트(.sb3) 저장/로드(W5) — async reply 패턴
  asyncRoutes["project-save-async"] = async (a) => {
    const [id, name, data] = a as [string, string, ArrayBuffer];
    await projectStore.save(id, name, data);
    return { success: true };
  };
  asyncRoutes["project-load-async"] = async (a) => {
    const data = await projectStore.load(String(a[0]));
    return data ?? null;
  };

  // 화면 캡처(W4): luka-capture-window → 가장 큰 캔버스 캡처(영상 비전송, 캡처는 로컬)
  handlers.set("luka-capture-window", async () => {
    const canvases = Array.from(document.querySelectorAll("canvas")) as HTMLCanvasElement[];
    const stage = canvases.sort((a, b) => b.width * b.height - a.width * a.height)[0];
    if (!stage) return { success: false, error: "no canvas" };
    try {
      return { success: true, dataUrl: captureToDataUrl(stage) };
    } catch (e) {
      return { success: false, error: (e as Error).message };
    }
  });

  // gemini(LUKA): invoke 또는 send 양쪽 대응 → "gemini-response"로 회신
  async function gemini(args: unknown[], withImage: boolean): Promise<unknown> {
    const prompt = String(args[0] ?? "");
    const imageDataUrl = withImage ? (args[1] as string | undefined) : undefined;
    try {
      const text = await askLuka({ prompt, imageDataUrl, getAuthToken: opts.getAuthToken });
      emit("gemini-response", { success: true, message: text });
      return { success: true, text };
    } catch (e) {
      emit("gemini-response", { success: false, message: (e as Error).message });
      return { success: false, error: (e as Error).message };
    }
  }

  // invoke 핸들러(Promise 반환 채널)
  handlers.set("extTypeList", async () => (await getExtentions()).extTypeList ?? []);
  handlers.set("extBlockList", async () => (await getExtentions()).extBlockList ?? {});
  handlers.set("examTypeList", async () => (await getExtentions()).examTypeList ?? []);
  handlers.set("examList", async () => (await getExtentions()).examList ?? {});
  handlers.set("blockData", async (extensionNameArg) => {
    const extensionName = String(extensionNameArg ?? "");
    if (!extensionName) return null;
    const data = await getExtentions();
    const extBlockList = data.extBlockList ?? {};
    const typeKey = findExtensionType(extensionName, extBlockList);
    if (!typeKey) return null;
    const res = await fetch(
      `/scratch-gui/static/serve/library/${typeKey}/${extensionName}/${extensionName}.json`,
      { cache: "no-cache" },
    );
    if (!res.ok) {
      throw new Error(`blockData 로드 실패(${extensionName}): HTTP ${res.status}`);
    }
    return res.json();
  });
  handlers.set("gemini-query", (...a) => gemini(a, false));
  handlers.set("gemini-query-with-screenshot", (...a) => gemini(a, true));
  handlers.set("luka-get-app-session-context", async () => ({ web: true }));

  const ipcRenderer = {
    send(channel: string, ...args: unknown[]) {
      // 비동기 reply 패턴: 첫 인자가 replyChannel
      if (channel in asyncRoutes) {
        const [replyChannel, ...rest] = args;
        asyncRoutes[channel](rest)
          .then((r) => emit(String(replyChannel), r))
          .catch((e) => emit(String(replyChannel), (e as Error).message));
        return;
      }
      if (channel in sendRoutes) {
        sendRoutes[channel](args);
        return;
      }
      if (channel in replyRoutes) {
        void replyRoutes[channel]((args[0] ?? {}) as { contentPath: string; tabType?: string });
        return;
      }
      // gemini를 send로 호출하는 레거시 경로
      if (channel === "gemini-query") void gemini(args, false);
      else if (channel === "gemini-query-with-screenshot") void gemini(args, true);
    },
    sendSync(channel: string, ...args: unknown[]): unknown {
      switch (channel) {
        case "isConnected":
          return device.connected &&
            (!args[0] || !connectedBoard || String(args[0]) === connectedBoard);
        case "getConnectedApp":
          return connectedApp;
        case "cs-os-homedir":
          return "/web";
        case "cs-process-argv":
          return [];
        case "cs-get-launch-sb3-path":
          return "";
        case "cs-files-ready":
          return true;
        case "cs-window-is-maximized":
          return true;
        case "cs-fs-read-file-sync": {
          const v = kv.get<string>(`fs:${String(args[0])}`);
          return v ? { ok: true, base64: v } : { ok: false, error: "not found" };
        }
        case "cs-fs-readdir-sync":
          return { ok: true, names: [] };
        default:
          return undefined;
      }
    },
    async invoke(channel: string, ...args: unknown[]): Promise<unknown> {
      const h = handlers.get(channel);
      if (h) return h(...args);
      if (channel in asyncRoutes) return asyncRoutes[channel](args);
      return undefined;
    },
    on(channel: string, listener: Listener) {
      if (!listeners.has(channel)) listeners.set(channel, new Set());
      listeners.get(channel)!.add(listener);
      return ipcRenderer;
    },
    once(channel: string, listener: Listener) {
      const wrap: Listener = (e, ...a) => {
        listeners.get(channel)?.delete(wrap);
        listener(e, ...a);
      };
      return ipcRenderer.on(channel, wrap);
    },
    removeListener(channel: string, listener: Listener) {
      listeners.get(channel)?.delete(listener);
      return ipcRenderer;
    },
    removeAllListeners(channel: string) {
      listeners.delete(channel);
      return ipcRenderer;
    },
    setMaxListeners() {},
  };

  (target as unknown as { electronAPI: unknown }).electronAPI = {
    ipcRenderer,
    remote: {
      getCurrentWindow: () => ({
        close() {},
        minimize() {},
        maximize() {},
        restore() {},
        isMaximized: () => true,
      }),
      dialog: {
        showMessageBox: (_w: unknown, _o: unknown, cb?: (r: number) => void) =>
          cb ? cb(0) : 0,
      },
    },
    __web__: true,
  };

  return { device };
}

function decodeWrite(v: unknown): string {
  if (typeof v === "string") return v;
  if (Array.isArray(v)) return new TextDecoder().decode(Uint8Array.from(v as number[]));
  return String(v ?? "");
}
